import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  CircularProgress,
  CardActions
} from '@mui/material';
import { useApi } from '../../hooks/useApi';

interface PaymentConfigProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface PaymentConfigData {
  defaultCurrency: string;
  allowedPaymentMethods: string[];
  mode: 'test' | 'live';
  status: 'active' | 'inactive';
  assessmentFee: number;
  stripeConfig: {
    publicKey: string;
    secretKeyConfigured: boolean;
    webhookSecretConfigured: boolean;
  };
  bankTransfer: {
    accountName: string;
    sortCode: string;
    accountNumber: string;
  };
}

const PaymentConfig: React.FC<PaymentConfigProps> = ({ onSuccess, onError }) => {
  const [config, setConfig] = useState<PaymentConfigData>({
    defaultCurrency: 'GBP',
    allowedPaymentMethods: ['card'],
    mode: 'test',
    status: 'inactive',
    assessmentFee: 15,
    stripeConfig: {
      publicKey: '',
      secretKeyConfigured: false,
      webhookSecretConfigured: false
    },
    bankTransfer: {
      accountName: '',
      sortCode: '',
      accountNumber: ''
    }
  });
  // Replacement values are held separately from the fetched config - the
  // server never sends the real secret back, so these always start empty
  // and are only submitted (and only overwrite the stored credential) if
  // the admin actually types a new value.
  const [newSecretKey, setNewSecretKey] = useState('');
  const [newWebhookSecret, setNewWebhookSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const api = useApi();
  // useApi returns a fresh object every render, and the parent passes inline
  // onSuccess/onError callbacks - keep refs to all of them so fetchConfig
  // stays referentially stable and the useEffect below doesn't re-fire on
  // every render (that previously caused a continuous fetch loop).
  const apiRef = useRef(api);
  apiRef.current = api;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRef.current.get<{ data: PaymentConfigData }>('/api/payments/admin-config');
      
      if (!response.error && response.data?.data) {
        const data = response.data.data;
        setConfig({
          ...data,
          // Older API versions omit these - normalize so the form never
          // dereferences undefined (e.g. selecting Bank Transfer before the
          // field exists on the server).
          allowedPaymentMethods: data.allowedPaymentMethods || ['card'],
          bankTransfer: data.bankTransfer || { accountName: '', sortCode: '', accountNumber: '' }
        });
      } else if (response.error) {
        throw new Error(response.error);
      }
    } catch (err) {
      console.error('Error fetching payment config:', err);
      setError('Failed to load payment configuration');
      onErrorRef.current('Failed to load payment configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setLoading(true);
      const response = await api.put('/api/payments/config', {
        defaultCurrency: config.defaultCurrency,
        allowedPaymentMethods: config.allowedPaymentMethods,
        mode: config.mode,
        status: config.status,
        assessmentFee: config.assessmentFee,
        bankTransfer: {
          accountName: config.bankTransfer.accountName,
          sortCode: config.bankTransfer.sortCode,
          accountNumber: config.bankTransfer.accountNumber
        },
        stripeConfig: {
          publicKey: config.stripeConfig.publicKey,
          // Omitted entirely unless the admin typed a replacement - the
          // server treats an omitted/empty value as "leave unchanged".
          ...(newSecretKey ? { secretKey: newSecretKey } : {}),
          ...(newWebhookSecret ? { webhookSecret: newWebhookSecret } : {})
        }
      });

      if (!response.error) {
        setError('');
        setNewSecretKey('');
        setNewWebhookSecret('');
        onSuccessRef.current('Payment configuration updated successfully');
        fetchConfig(); // Refresh the config
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      console.error('Error updating payment config:', err);
      onErrorRef.current('Failed to update payment configuration');
      setError('Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
      <form onSubmit={handleSubmit}>
        <CardContent>
          <Typography variant="h6" gutterBottom className="theme-text-primary">
            Payment Configuration
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.9)', '&.Mui-focused': { color: 'rgba(100, 181, 246, 0.9)' } }}>Default Currency</InputLabel>
                <Select
                  value={config.defaultCurrency}
                  onChange={(e) => setConfig({ ...config, defaultCurrency: e.target.value })}
                  label="Default Currency"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                  }}
                >
                  <MenuItem value="GBP">British Pound (GBP)</MenuItem>
                  <MenuItem value="USD">US Dollar (USD)</MenuItem>
                  <MenuItem value="EUR">Euro (EUR)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.9)', '&.Mui-focused': { color: 'rgba(100, 181, 246, 0.9)' } }}>Allowed Payment Methods</InputLabel>
                <Select
                  multiple
                  value={config.allowedPaymentMethods}
                  onChange={(e) => setConfig({ ...config, allowedPaymentMethods: e.target.value as string[] })}
                  label="Allowed Payment Methods"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value === 'card' ? 'Credit/Debit Card' : 'Bank Transfer'} />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="card">Credit/Debit Card</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Assessment Fee (£)"
                value={config.assessmentFee}
                onChange={(e) => setConfig({ ...config, assessmentFee: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Charged to direct-referral clients on top of the session rate"
                sx={{
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.9)' },
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                  },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Mode
              </Typography>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.9)', '&.Mui-focused': { color: 'rgba(100, 181, 246, 0.9)' } }}>Mode</InputLabel>
                <Select
                  value={config.mode}
                  onChange={(e) => setConfig({ ...config, mode: e.target.value as 'test' | 'live' })}
                  label="Mode"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                  }}
                >
                  <MenuItem value="test">Test Mode</MenuItem>
                  <MenuItem value="live">Live Mode</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Payment System Status
              </Typography>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.9)', '&.Mui-focused': { color: 'rgba(100, 181, 246, 0.9)' } }}>Status</InputLabel>
                <Select
                  value={config.status}
                  onChange={(e) => setConfig({ ...config, status: e.target.value as 'active' | 'inactive' })}
                  label="Status"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                  }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Stripe Configuration
              </Typography>
              
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Publishable Key"
                  value={config.stripeConfig.publicKey}
                  onChange={(e) => setConfig({
                    ...config,
                    stripeConfig: {
                      ...config.stripeConfig,
                      publicKey: e.target.value
                    }
                  })}
                  placeholder="Enter Publishable Key"
                  helperText="Your Stripe Publishable Key (safe to display - this is not a secret)"
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.9)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                    },
                    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
                  }}
                />

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Secret Key:</Typography>
                    <Chip
                      size="small"
                      label={config.stripeConfig.secretKeyConfigured ? 'Configured ✓' : 'Not configured'}
                      color={config.stripeConfig.secretKeyConfigured ? 'success' : 'default'}
                    />
                  </Stack>
                  <TextField
                    fullWidth
                    type="password"
                    label="Replace Secret Key"
                    value={newSecretKey}
                    onChange={(e) => setNewSecretKey(e.target.value)}
                    placeholder="Leave blank to keep the existing secret key"
                    helperText="Enter a new value only to rotate/replace the stored Stripe Secret Key. It is never shown here once saved."
                    sx={{
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.9)' },
                      '& .MuiOutlinedInput-root': {
                        color: 'rgba(255, 255, 255, 0.9)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                      },
                      '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
                    }}
                  />
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Webhook Secret:</Typography>
                    <Chip
                      size="small"
                      label={config.stripeConfig.webhookSecretConfigured ? 'Configured ✓' : 'Not configured'}
                      color={config.stripeConfig.webhookSecretConfigured ? 'success' : 'default'}
                    />
                  </Stack>
                  <TextField
                    fullWidth
                    type="password"
                    label="Replace Webhook Secret"
                    value={newWebhookSecret}
                    onChange={(e) => setNewWebhookSecret(e.target.value)}
                    placeholder="Leave blank to keep the existing webhook secret"
                    helperText="Enter a new value only to rotate/replace the stored Stripe Webhook Secret. It is never shown here once saved."
                    sx={{
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.9)' },
                      '& .MuiOutlinedInput-root': {
                        color: 'rgba(255, 255, 255, 0.9)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                      },
                      '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
                    }}
                  />
                </Box>
              </Stack>
            </Grid>

            {config.allowedPaymentMethods.includes('bank_transfer') && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Bank Transfer Details
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 1 }}>
                  Shown on payment request emails so clients can pay by bank transfer as an alternative to card.
                </Typography>
                <Stack spacing={2}>
                  {(['accountName', 'sortCode', 'accountNumber'] as const).map((field) => (
                    <TextField
                      key={field}
                      fullWidth
                      label={field === 'accountName' ? 'Account Name (company name)' : field === 'sortCode' ? 'Sort Code' : 'Account Number'}
                      value={config.bankTransfer[field]}
                      onChange={(e) => setConfig({
                        ...config,
                        bankTransfer: { ...config.bankTransfer, [field]: e.target.value }
                      })}
                      placeholder={field === 'sortCode' ? '12-34-56' : field === 'accountNumber' ? '12345678' : 'Evolving Minds Ltd'}
                      sx={{
                        '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.9)' },
                        '& .MuiOutlinedInput-root': {
                          color: 'rgba(255, 255, 255, 0.9)',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </CardActions>
      </form>
    </Card>
  );
};

export default PaymentConfig;
